import React, { useState, useEffect } from 'react';
import { api } from '../App';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [pushToSheets, setPushToSheets] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    loadStats();
    loadSettings();
  }, []);

  const loadStats = async () => {
    try {
      const response = await api.get('/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  };

  const startScrape = async (e) => {
    e.preventDefault();
    setScraping(true);
    setProgress(null);
    try {
      const response = await api.post('/scrape', { date, pushToSheets });
      const { jobId } = response.data;

      // Use polling instead of SSE for better compatibility
      const pollProgress = async () => {
        try {
          const statusRes = await api.get(`/scrape/status/${jobId}`);
          const data = statusRes.data;
          setProgress(data);

          if (data.status === 'completed' || data.status === 'error') {
            setScraping(false);
            loadStats();
            return; // Stop polling
          }

          // Continue polling every 300ms
          setTimeout(pollProgress, 300);
        } catch (err) {
          console.error('Polling error:', err);
          setScraping(false);
        }
      };

      // Start polling
      pollProgress();
    } catch (err) {
      console.error('Failed to start scrape:', err);
      setScraping(false);
    }
  };

  const syncToSheets = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/sync-sheets');
      const results = response.data.results || [];
      const successCount = results.filter(r => r.status === 'success').length;
      const errorCount = results.filter(r => r.status === 'error').length;

      if (errorCount > 0) {
        const errors = results.filter(r => r.status === 'error').map(r => `${r.date}: ${r.error}`).join('\n');
        alert(`Sync completed with errors:\n${successCount} dates synced successfully\n${errorCount} dates failed:\n${errors}`);
      } else if (successCount > 0) {
        alert(`Successfully synced ${successCount} date(s) to Google Sheets!`);
      } else {
        alert('No data to sync. Run a scrape first.');
      }
    } catch (err) {
      console.error('Sync error:', err);
      alert('Failed to sync: ' + (err.response?.data?.error || err.message));
    } finally {
      setSyncing(false);
    }
  };

  const phases = [
    { key: 'fetching', label: 'Fetching List', icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' },
    { key: 'scraping', label: 'Downloading PDFs', icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' },
    { key: 'parsing', label: 'Parsing with AI', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
    { key: 'cleanup', label: 'Cleaning Up', icon: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' },
    { key: 'sheets', label: 'Syncing to Sheets', icon: 'M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12' },
    { key: 'done', label: 'Complete', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
  ];

  const getPhaseIndex = (phase) => phases.findIndex(p => p.key === phase);
  const getCurrentPhase = () => phases.find(p => p.key === progress?.phase) || phases[0];
  const getProgressPercent = () => (!progress?.total ? 0 : Math.round((progress.progress / progress.total) * 100));

  const statCards = [
    { label: 'Total Runs', value: stats?.totalRuns || 0, icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', color: 'primary' },
    { label: 'Companies', value: stats?.totalCompanies || 0, icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', color: 'success' },
    { label: 'With Founders', value: stats?.companiesWithFounders || 0, icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z', color: 'warning' },
    { label: 'API Cost', value: `€${(stats?.totalApiCost || 0).toFixed(4)}`, icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z', color: 'danger' },
  ];

  const colorClasses = {
    primary: { bg: 'bg-primary-500', light: 'bg-primary-50', text: 'text-primary-500' },
    success: { bg: 'bg-success-500', light: 'bg-success-500/10', text: 'text-success-500' },
    warning: { bg: 'bg-warning-500', light: 'bg-warning-500/10', text: 'text-warning-500' },
    danger: { bg: 'bg-danger-500', light: 'bg-danger-500/10', text: 'text-danger-500' },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-default-900">Dashboard</h1>
          <p className="text-default-500 mt-1">Overview of your scraping activity</p>
        </div>
        {settings?.sheetsConfigured && (
          <button
            onClick={syncToSheets}
            disabled={syncing}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-default-200 rounded-2xl text-default-700 hover:bg-default-50 disabled:opacity-50 transition-all shadow-sm font-medium"
          >
            <svg className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            {syncing ? 'Syncing...' : 'Sync to Sheets'}
          </button>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <div key={stat.label} className="bg-white rounded-2xl p-5 shadow-card card">
            <div className={`w-12 h-12 ${colorClasses[stat.color].light} rounded-2xl flex items-center justify-center mb-4`}>
              <svg className={`w-6 h-6 ${colorClasses[stat.color].text}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={stat.icon} />
              </svg>
            </div>
            <p className="text-sm text-default-500 font-medium">{stat.label}</p>
            <p className="text-2xl font-bold text-default-900 mt-1">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Manual Scrape Card */}
      <div className="bg-white rounded-2xl shadow-card p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-primary-50 rounded-2xl flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-default-900">Manual Scrape</h2>
            <p className="text-default-500 text-sm mt-0.5">Download and parse company data for a specific date</p>
          </div>
        </div>

        <form onSubmit={startScrape} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-default-700 mb-2">Publication Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={scraping}
                className="w-full px-4 py-3 bg-default-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 transition-all"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-default-100 rounded-xl w-full">
                <input
                  type="checkbox"
                  checked={pushToSheets}
                  onChange={(e) => setPushToSheets(e.target.checked)}
                  disabled={scraping || !settings?.sheetsConfigured}
                  className="w-5 h-5 text-primary-500 rounded-lg border-default-300 focus:ring-primary-500"
                />
                <span className="text-sm text-default-700 font-medium">Push to Google Sheets</span>
              </label>
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={scraping}
                className="w-full px-6 py-3 btn-primary text-white font-semibold rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {scraping ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Start Scrape
                  </>
                )}
              </button>
            </div>
          </div>

          {settings?.scraper?.pdfLimit > 0 && (
            <div className="flex items-center gap-2 text-sm text-default-500">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Limited to {settings.scraper.pdfLimit} PDFs per scan
            </div>
          )}
        </form>

        {/* Progress Section */}
        {progress && (
          <div className="mt-6 p-5 bg-default-50 rounded-2xl">
            {/* Phase Steps */}
            <div className="flex items-center justify-between mb-6">
              {phases.slice(0, -1).map((phase, index) => {
                const currentIndex = getPhaseIndex(progress.phase);
                const isActive = phase.key === progress.phase;
                const isCompleted = index < currentIndex || progress.status === 'completed';
                const isError = progress.status === 'error' && isActive;

                return (
                  <React.Fragment key={phase.key}>
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                        isError ? 'bg-danger-500 text-white' :
                        isCompleted ? 'bg-success-500 text-white' :
                        isActive ? 'bg-primary-500 text-white' :
                        'bg-default-200 text-default-400'
                      }`}>
                        {isCompleted && !isActive ? (
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className={`w-5 h-5 ${isActive && !isError ? 'animate-pulse' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={phase.icon} />
                          </svg>
                        )}
                      </div>
                      <span className={`text-xs mt-2 font-medium hidden sm:block ${
                        isActive ? 'text-primary-600' : isCompleted ? 'text-success-600' : 'text-default-400'
                      }`}>
                        {phase.label}
                      </span>
                    </div>
                    {index < phases.length - 2 && (
                      <div className={`flex-1 h-1 mx-2 rounded-full transition-all ${
                        index < currentIndex || progress.status === 'completed' ? 'bg-success-500' : 'bg-default-200'
                      }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Current Status */}
            <div className="bg-white rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${
                    progress.status === 'completed' ? 'bg-success-500/10 text-success-600' :
                    progress.status === 'error' ? 'bg-danger-500/10 text-danger-600' :
                    'bg-primary-500/10 text-primary-600'
                  }`}>
                    {getCurrentPhase().label}
                  </span>
                  {progress.skippedExisting > 0 && (
                    <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-default-100 text-default-600">
                      {progress.skippedExisting} already in DB
                    </span>
                  )}
                </div>
                {progress.total > 0 && (
                  <span className="text-sm font-semibold text-default-700">
                    {progress.progress || 0} / {progress.total}
                  </span>
                )}
              </div>

              {/* Progress Bar */}
              {progress.total > 0 && (
                <div className="h-3 bg-default-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 rounded-full ${
                      progress.status === 'completed' ? 'bg-success-500' :
                      progress.status === 'error' ? 'bg-danger-500' :
                      'bg-primary-500'
                    }`}
                    style={{ width: `${getProgressPercent()}%` }}
                  />
                </div>
              )}

              <p className="text-sm text-default-600 mt-2">{progress.message}</p>
            </div>

            {/* Stats Grid */}
            {(progress.status === 'completed' || progress.totalFound > 0) && (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[
                  { label: 'Found', value: progress.totalFound || 0, show: true },
                  { label: 'Skipped', value: progress.skippedExisting || 0, show: (progress.skippedExisting || 0) > 0, color: 'text-default-500' },
                  { label: 'New', value: progress.newCompanies || progress.scraped || 0, show: true, color: 'text-primary-600' },
                  { label: 'Parsed', value: progress.parsed || 0, show: progress.status === 'completed', color: 'text-success-600' },
                  { label: 'Cost', value: `€${(progress.apiCost || 0).toFixed(4)}`, show: progress.status === 'completed' },
                ].filter(s => s.show).map((s) => (
                  <div key={s.label} className="bg-white rounded-xl p-3 text-center">
                    <p className={`text-lg font-bold ${s.color || 'text-default-900'}`}>{s.value}</p>
                    <p className="text-xs text-default-500">{s.label}</p>
                  </div>
                ))}
              </div>
            )}

            {progress.status === 'error' && (
              <div className="mt-3 p-3 bg-danger-500/10 rounded-xl">
                <p className="text-sm text-danger-600">{progress.error}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Last Run Card */}
      {stats?.lastRun && (
        <div className="bg-white rounded-2xl shadow-card p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-success-500/10 rounded-2xl flex items-center justify-center">
              <svg className="w-6 h-6 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-default-500">Last Successful Run</p>
              <p className="text-default-900 font-semibold">{stats.lastRun.date} — {stats.lastRun.parsed || 0} companies parsed</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
